import * as RadixDialog from "@radix-ui/react-dialog";
import type { CSSProperties, ReactNode } from "react";

// Thin styled wrapper around @radix-ui/react-dialog. Radix handles focus trap,
// ESC, scroll lock, and click-outside; we paint the surface with the project's
// tokens to match the Popover / Button look.

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  width?: number;
}

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  width = 440,
}: DialogProps) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay style={overlayStyle} />
        <RadixDialog.Content style={{ ...contentStyle, width }}>
          <RadixDialog.Title style={titleStyle}>{title}</RadixDialog.Title>
          {description ? (
            <RadixDialog.Description style={descriptionStyle}>
              {description}
            </RadixDialog.Description>
          ) : (
            <RadixDialog.Description style={{ display: "none" }}>{title}</RadixDialog.Description>
          )}
          {children}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginTop: 20,
      }}
    >
      {children}
    </div>
  );
}

const overlayStyle: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(13,62,99,0.32)",
  backdropFilter: "blur(2px)",
  zIndex: 300,
  animation: "flow-dialog-fade-in 120ms ease-out",
};

const contentStyle: CSSProperties = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: "var(--surface-elev)",
  borderRadius: 12,
  border: "1px solid var(--hairline-strong)",
  padding: "22px 24px 24px",
  boxShadow: "0 20px 60px rgba(13,62,99,0.18), 0 4px 12px rgba(13,62,99,0.08)",
  zIndex: 301,
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  color: "var(--ink-1)",
  maxWidth: "calc(100vw - 32px)",
  maxHeight: "calc(100vh - 32px)",
  overflow: "auto",
};

const titleStyle: CSSProperties = {
  margin: 0,
  fontSize: 17,
  fontWeight: 600,
  letterSpacing: "-0.01em",
  color: "var(--ink-1)",
};

const descriptionStyle: CSSProperties = {
  margin: "4px 0 16px",
  fontSize: 13,
  color: "var(--ink-3)",
  lineHeight: 1.4,
};

interface FieldProps {
  label: string;
  htmlFor: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}

export function DialogField({ label, htmlFor, hint, error, children }: FieldProps) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label
        htmlFor={htmlFor}
        style={{
          display: "block",
          fontSize: 12,
          fontWeight: 500,
          color: "var(--ink-2)",
          marginBottom: 5,
        }}
      >
        {label}
      </label>
      {children}
      {hint && !error ? (
        <div style={{ fontSize: 11.5, color: "var(--ink-4)", marginTop: 5 }}>{hint}</div>
      ) : null}
      {error ? (
        <div style={{ fontSize: 11.5, color: "var(--danger)", marginTop: 5 }}>{error}</div>
      ) : null}
    </div>
  );
}

export const dialogInputStyle: CSSProperties = {
  width: "100%",
  height: 36,
  padding: "0 10px",
  fontSize: 13.5,
  borderRadius: 7,
  border: "1px solid var(--hairline-strong)",
  background: "var(--surface)",
  color: "var(--ink-1)",
  fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
  outline: "none",
};

export const dialogTextareaStyle: CSSProperties = {
  ...dialogInputStyle,
  height: 84,
  padding: "8px 10px",
  resize: "vertical",
  fontFamily: "inherit",
  lineHeight: 1.45,
};
